/**
 * Cold Chain Checker
 * 
 * Manages postal code validation and delivery zone information for cold chain products.
 * This class handles user input, validates postal codes against configured zones,
 * and controls cart functionality based on delivery availability.
 * 
 * @class ColdChainChecker
 * @version 2.0.0
 */

class ColdChainChecker {
  /**
   * @param {Object} config - Configuration object containing zones and settings
   */
  constructor(config) {
    if (!config || !config.zones || !config.settings) {
      console.error('ColdChainChecker: Invalid configuration');
      return;
    }

    this.config = config;
    this.currentPostalCode = null;
    this.currentZone = null;
    this.elements = this.cacheElements();
    this.debug = config.settings.enableDebug || false;
    
    this.init();
  }

  /**
   * Cache DOM elements for better performance
   * @returns {Object} Object containing cached DOM elements
   */
  cacheElements() {
    return {
      container: document.getElementById('postalcode-selector'),
      input: document.getElementById('postalcode-selector__input'),
      feedback: document.getElementById('postalcode-selector__feedback'),
      cartButtons: document.querySelectorAll('.cart__checkout'),
      productCartButtons: document.querySelectorAll('.btn.add-to-cart'),
      ajaxCartInput: document.getElementById('ajaxcart-postalcode-selector__input'),
      ajaxCartFeedback: document.getElementById('ajaxcart-postalcode-selector__feedback'),
      ajaxCartContainer: document.getElementById('ajaxcart-postalcode-selector'),
      continueShopping: document.getElementById('continueShopping'),
      coldChainWrapper: document.getElementById('ajaxcart__coldchain-wrapper')
    };
  }

  /**
   * Initialize the checker
   */
  init() {
    this.log('Initializing Cold Chain Checker');
    
    const storedPostalCode = this.getStoredPostalCode();
    
    if (storedPostalCode) {
      this.log(`Found stored postal code: ${storedPostalCode}`);
      this.setPostalCode(storedPostalCode);
    } else {
      this.log('No stored postal code found');
      this.disableCartFunctionality();
    }

    this.bindEvents();
    this.log('Cold Chain Checker initialized');
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (this.elements.input) {
      // Sanitize input to only allow numbers
      this.elements.input.addEventListener('input', this.handleInput.bind(this));
      this.elements.input.addEventListener('keypress', this.handleKeyPress.bind(this));
      this.elements.input.addEventListener('focus', this.handleFocus.bind(this));
      this.elements.input.addEventListener('blur', this.handleBlur.bind(this));
    }

    // Listen for ajax cart input as well
    if (this.elements.ajaxCartInput) {
      this.elements.ajaxCartInput.addEventListener('input', this.handleInput.bind(this));
      this.elements.ajaxCartInput.addEventListener('keypress', this.handleKeyPress.bind(this));
    }

    // Custom event for postal code updates across the app
    document.addEventListener('postalcode:updated', this.handlePostalCodeUpdate.bind(this));
  }

  /**
   * Handle keypress to allow only numeric input
   * @param {KeyboardEvent} event
   */
  handleKeyPress(event) {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow only numbers (0-9)
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Handle input changes
   * @param {InputEvent} event
   */
  handleInput(event) {
    const rawValue = event.target.value;
    // Sanitize: remove non-numeric characters
    const postalCode = rawValue.replace(/\D/g, '').substring(0, this.config.settings.postalCodeLength);
    
    // Update input value if it was sanitized
    if (event.target.value !== postalCode) {
      event.target.value = postalCode;
    }
    
    this.validateAndSetPostalCode(postalCode);
  }

  /**
   * Handle input focus
   */
  handleFocus() {
    this.elements.container?.classList.add('postalcode-selector--focused');
    this.elements.ajaxCartContainer?.classList.add('postalcode-selector--focused');
  }

  /**
   * Handle input blur
   */
  handleBlur() {
    this.elements.container?.classList.remove('postalcode-selector--focused');
    this.elements.ajaxCartContainer?.classList.remove('postalcode-selector--focused');
  }

  /**
   * Handle custom postal code update event
   * @param {CustomEvent} event
   */
  handlePostalCodeUpdate(event) {
    const postalCode = event.detail?.postalCode || '';
    this.log(`Postal code updated via event: ${postalCode}`);
    this.syncAjaxCart(postalCode);
  }

  /**
   * Validate and set postal code
   * @param {string} postalCode
   */
  validateAndSetPostalCode(postalCode) {
    const expectedLength = this.config.settings.postalCodeLength;
    
    // Clear validation if input is empty or incomplete
    if (!postalCode || postalCode.length < expectedLength) {
      this.clearValidation();
      this.disableCartFunctionality();
      this.hideFeedback(); // Hide feedback when incomplete
      return;
    }

    // Only proceed if we have exactly 5 digits
    if (postalCode.length !== expectedLength) {
      return;
    }

    this.log(`Validating postal code: ${postalCode}`);
    const deliveryZone = this.findDeliveryZone(postalCode);
    
    if (deliveryZone) {
      this.log(`Found delivery zone: ${deliveryZone.id}`);
      this.setValidState(postalCode, deliveryZone);
    } else {
      this.log(`No delivery zone found for: ${postalCode}`);
      this.setInvalidState(postalCode);
    }

    this.storePostalCode(postalCode);
    this.dispatchUpdateEvent(postalCode, deliveryZone);
  }

  /**
   * Find delivery zone for a given postal code
   * @param {string} postalCode
   * @returns {Object|null} The matching zone or null
   */
  findDeliveryZone(postalCode) {
    if (!postalCode || typeof postalCode !== 'string') {
      return null;
    }

    // Normalize postal code (remove whitespace)
    const normalizedCode = postalCode.trim();
    
    this.log(`🔍 Looking for postal code: "${normalizedCode}"`);
    
    // Sort zones by priority to handle overlapping postal codes correctly
    const sortedZones = [...this.config.zones].sort((a, b) => a.priority - b.priority);
    
    this.log(`📋 Checking ${sortedZones.length} zones in priority order`);
    
    for (const zone of sortedZones) {
      if (zone.postalCodes && Array.isArray(zone.postalCodes)) {
        this.log(`Checking zone: ${zone.id} (priority: ${zone.priority}) with ${zone.postalCodes.length} postal codes`);
        
        // Check if postal code exists in this zone
        // Defensive check: ensure code exists, is a string, and trim it
        const found = zone.postalCodes.some(code => {
          if (!code || typeof code !== 'string') {
            this.log(`⚠️ Invalid postal code in zone ${zone.id}:`, code);
            return false;
          }
          const trimmedCode = code.trim();
          const matches = trimmedCode === normalizedCode;
          
          if (this.debug && matches) {
            this.log(`✅ MATCH FOUND in zone ${zone.id}: "${trimmedCode}" === "${normalizedCode}"`);
          }
          
          return matches;
        });
        
        if (found) {
          this.log(`🎯 Returning zone: ${zone.id}`);
          return zone;
        } else {
          this.log(`❌ No match in zone: ${zone.id}`);
        }
      } else {
        this.log(`⚠️ Zone ${zone.id} has invalid postalCodes array`);
      }
    }

    this.log(`❌ No delivery zone found for postal code: ${normalizedCode}`);
    return null;
  }

  /**
   * Set valid state for postal code
   * @param {string} postalCode
   * @param {Object} zone
   */
  setValidState(postalCode, zone) {
    this.currentPostalCode = postalCode;
    this.currentZone = zone;
    this.addValidationClass('valid');
    this.showFeedback(zone.message, zone.margin);
    this.enableCartFunctionality();
  }

  /**
   * Set invalid state for postal code
   * @param {string} postalCode
   */
  setInvalidState(postalCode) {
    this.currentPostalCode = postalCode;
    this.currentZone = null;
    this.addValidationClass('invalid');
    
    // Show fallback message for non-cold chain delivery
    const fallbackMessage = this.config.settings.nonColdChainMessage || 
                           this.config.zones.find(z => z.fallback)?.message || 
                           'Delivery not available for this postal code';
    
    this.showFeedback(fallbackMessage, '0em');
    this.disableCartFunctionality();
  }

  addValidationClass(state) {
    const className = `postalcode-selector--${state}`;
    const oppositeClass = `postalcode-selector--${state === 'valid' ? 'invalid' : 'valid'}`;

    [this.elements.container, this.elements.ajaxCartContainer].forEach(element => {
      if (element) {
        element.classList.remove(oppositeClass);
        element.classList.add(className);
      }
    });
  }

  clearValidation() {
    ['postalcode-selector--valid', 'postalcode-selector--invalid'].forEach(className => {
      [this.elements.container, this.elements.ajaxCartContainer].forEach(element => {
        element?.classList.remove(className);
      });
    });
  }

  /**
   * Show feedback message and make it visible
   * @param {string} message - The message to display
   * @param {string} margin - Top margin for spacing
   */
  showFeedback(message) {
    [this.elements.feedback, this.elements.ajaxCartFeedback].forEach(element => {
      if (element) {
        element.innerHTML = message;
        element.style.display = ''; // Show the element
      }
    });
  }

  /**
   * Hide feedback element (used when input is incomplete)
   */
  hideFeedback() {
    [this.elements.feedback, this.elements.ajaxCartFeedback].forEach(element => {
      if (element) {
        element.style.display = 'none';
      }
    });

    // Reset margin for continue shopping button
    if (this.elements.continueShopping) {
      this.elements.continueShopping.style.marginTop = '2em';
    }
  }

  enableCartFunctionality() {
    this.elements.cartButtons.forEach(btn => {
      btn.disabled = false;
    });

    this.elements.productCartButtons.forEach(btn => {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalText || 'Add to Cart';
    });

    if (this.elements.coldChainWrapper) {
      this.elements.coldChainWrapper.style.display = 'none';
    }
  }

  disableCartFunctionality() {
    this.elements.cartButtons.forEach(btn => {
      btn.disabled = true;
    });

    this.elements.productCartButtons.forEach(btn => {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent;
      }
      btn.disabled = true;
      btn.textContent = 'Enter postal code';
    });

    if (this.elements.coldChainWrapper) {
      this.elements.coldChainWrapper.style.display = 'block';
    }
  }

  syncAjaxCart(postalCode) {
    if (this.elements.ajaxCartInput) {
      this.elements.ajaxCartInput.value = postalCode;
    }
  }

  setPostalCode(postalCode) {
    if (this.elements.input) {
      this.elements.input.value = postalCode;
    }
    this.validateAndSetPostalCode(postalCode);
    this.syncAjaxCart(postalCode);
  }

  getStoredPostalCode() {
    try {
      const stored = localStorage.getItem(this.config.settings.storageKey);
      return stored && stored.length === 5 ? stored : null;
    } catch (e) {
      console.warn('Could not access localStorage:', e);
      return null;
    }
  }

  storePostalCode(postalCode) {
    try {
      if (postalCode && postalCode.length === 5) {
        localStorage.setItem(this.config.settings.storageKey, postalCode);
      } else {
        localStorage.removeItem(this.config.settings.storageKey);
      }
    } catch (e) {
      console.warn('Could not access localStorage:', e);
    }
  }

  /**
   * Dispatch update event
   * @param {string} postalCode
   * @param {Object|null} zone
   */
  dispatchUpdateEvent(postalCode, zone = null) {
    document.dispatchEvent(new CustomEvent('postalcode:updated', {
      detail: { 
        postalCode,
        zone,
        isValid: zone !== null
      }
    }));
    
    this.log(`Dispatched postalcode:updated event`, { postalCode, zone });
  }

  /**
   * Debug logging
   * @param {string} message
   * @param {*} data
   */
  log(message, data = null) {
    if (this.debug) {
      if (data) {
        console.log(`[ColdChainChecker] ${message}`, data);
      } else {
        console.log(`[ColdChainChecker] ${message}`);
      }
    }
  }

  // ==========================================
  // Public API methods
  // ==========================================

  /**
   * Get the current postal code
   * @returns {string|null}
   */
  getCurrentPostalCode() {
    return this.currentPostalCode;
  }

  /**
   * Get the current delivery zone
   * @returns {Object|null}
   */
  getCurrentZone() {
    return this.currentZone;
  }

  /**
   * Check if current postal code is valid
   * @returns {boolean}
   */
  hasValidPostalCode() {
    return this.currentPostalCode && this.currentZone !== null;
  }

  /**
   * Get delivery information for a postal code
   * @param {string} postalCode - Optional postal code, uses current if not provided
   * @returns {Object|null}
   */
  getDeliveryInfo(postalCode = this.currentPostalCode) {
    if (!postalCode) return null;
    return this.findDeliveryZone(postalCode);
  }

  /**
   * Manually set postal code (useful for testing or external integrations)
   * @param {string} postalCode
   */
  setPostalCodeManually(postalCode) {
    this.setPostalCode(postalCode);
  }

  /**
   * Clear current postal code
   */
  clearPostalCode() {
    this.currentPostalCode = null;
    this.currentZone = null;
    
    if (this.elements.input) {
      this.elements.input.value = '';
    }
    if (this.elements.ajaxCartInput) {
      this.elements.ajaxCartInput.value = '';
    }
    
    this.clearValidation();
    this.hideFeedback(); // Hide feedback when clearing
    this.disableCartFunctionality();
    this.storePostalCode('');
    this.log('Postal code cleared');
  }

  /**
   * Check if product requires cold chain delivery
   * @param {Array|string} productTags - Product tags array or comma-separated string
   * @returns {boolean}
   */
  requiresColdChain(productTags) {
    const coldChainTag = this.config.settings.productTag;
    
    if (!productTags) return false;
    
    if (typeof productTags === 'string') {
      productTags = productTags.split(',').map(tag => tag.trim());
    }
    
    return productTags.includes(coldChainTag);
  }

  // ==========================================
  // Static methods
  // ==========================================

  /**
   * Static method to check if product requires cold chain
   * @param {Array|string} productTags
   * @param {string} coldChainTag
   * @returns {boolean}
   */
  static requiresColdChain(productTags, coldChainTag) {
    if (!productTags || !coldChainTag) return false;
    
    if (typeof productTags === 'string') {
      productTags = productTags.split(',').map(tag => tag.trim());
    }
    
    return productTags.includes(coldChainTag);
  }
}

// ==========================================
// Initialize when DOM is ready
// ==========================================
(function() {
  'use strict';
  
  function initColdChainChecker() {
    if (window.coldChainConfig) {
      window.coldChainChecker = new ColdChainChecker(window.coldChainConfig);
      
      // Make it accessible globally for debugging
      if (window.coldChainConfig.settings.enableDebug) {
        console.log('[ColdChainChecker] Instance created and available as window.coldChainChecker');
      }
    } else {
      console.warn('[ColdChainChecker] Configuration not found. Ensure cold-chain-data.liquid is included.');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColdChainChecker);
  } else {
    initColdChainChecker();
  }
})();
